import { type FC, type KeyboardEvent, type MouseEvent, useMemo, useRef, useState } from 'react';
import IconMdi from '@components/IconMdi';
import { InputField } from '@components/InputField';
import {
  mdiCodeTags,
  mdiFormatBold,
  mdiFormatHeader1,
  mdiFormatHeader2,
  mdiFormatHeader3,
  mdiFormatItalic,
  mdiFormatListBulleted,
  mdiFormatListNumbered,
  mdiFormatQuoteClose,
  mdiLinkVariant,
  mdiXml,
} from '@mdi/js';
import clsx from 'clsx';

import { renderMarkdown } from '../utils/render-markdown';

type ToolbarAction =
  | { kind: 'wrap'; label: string; iconPath: string; before: string; after: string; placeholder?: string }
  | { kind: 'linePrefix'; label: string; iconPath: string; prefix: string }
  | { kind: 'link'; label: string; iconPath: string }
  | { kind: 'codeBlock'; label: string; iconPath: string };

const TOOLBAR_ACTIONS: ToolbarAction[] = [
  { kind: 'linePrefix', label: 'Heading 1', iconPath: mdiFormatHeader1, prefix: '# ' },
  { kind: 'linePrefix', label: 'Heading 2', iconPath: mdiFormatHeader2, prefix: '## ' },
  { kind: 'linePrefix', label: 'Heading 3', iconPath: mdiFormatHeader3, prefix: '### ' },
  { kind: 'wrap', label: 'Bold', iconPath: mdiFormatBold, before: '**', after: '**', placeholder: 'bold text' },
  { kind: 'wrap', label: 'Italic', iconPath: mdiFormatItalic, before: '_', after: '_', placeholder: 'italic text' },
  { kind: 'linePrefix', label: 'Bulleted list', iconPath: mdiFormatListBulleted, prefix: '- ' },
  { kind: 'linePrefix', label: 'Numbered list', iconPath: mdiFormatListNumbered, prefix: '1. ' },
  { kind: 'linePrefix', label: 'Quote', iconPath: mdiFormatQuoteClose, prefix: '> ' },
  { kind: 'wrap', label: 'Inline code', iconPath: mdiCodeTags, before: '`', after: '`', placeholder: 'code' },
  { kind: 'codeBlock', label: 'Code block', iconPath: mdiXml },
  { kind: 'link', label: 'Link', iconPath: mdiLinkVariant },
];

const MIN_ROWS = 14;

const HEADING_PREFIX_RE = /^(#{1,6})\s+/;
const LIST_BULLET_PREFIX_RE = /^(\s*)([-*])\s+/;
const LIST_NUMBERED_PREFIX_RE = /^(\s*)(\d+)\.\s+/;
const QUOTE_PREFIX_RE = /^>\s+/;

/**
 * Apply a line-level markdown prefix idempotently.
 * - Clicking the same prefix again toggles it off.
 * - Switching between prefixes of the same family (e.g. H1 -> H2, bullet -> numbered)
 *   replaces the previous prefix rather than stacking it, so "# Foo" + H2 becomes
 *   "## Foo", not "## # Foo".
 */
const transformLineForPrefix = (line: string, prefix: string): string => {
  if (line.startsWith(prefix)) {
    return line.slice(prefix.length);
  }

  const isHeadingPrefix = /^#{1,6}\s+$/.test(prefix);
  const isBulletPrefix = prefix === '- ' || prefix === '* ';
  const isNumberedPrefix = /^\d+\.\s+$/.test(prefix);
  const isQuotePrefix = prefix === '> ';

  let stripped = line;
  if (isHeadingPrefix) {
    stripped = stripped.replace(HEADING_PREFIX_RE, '');
  } else if (isBulletPrefix || isNumberedPrefix) {
    stripped = stripped.replace(LIST_BULLET_PREFIX_RE, '$1').replace(LIST_NUMBERED_PREFIX_RE, '$1');
  } else if (isQuotePrefix) {
    stripped = stripped.replace(QUOTE_PREFIX_RE, '');
  }

  return `${prefix}${stripped}`;
};

interface MarkdownEditorProps {
  labelText?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  descriptionText?: string;
  isRequired?: boolean;
  errors?: string;
  dataTestId?: string;
  name?: string;
  minRows?: number;
}

type Mode = 'edit' | 'preview';

const countWords = (input: string) =>
  input
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0).length;

export const MarkdownEditor: FC<MarkdownEditorProps> = ({
  labelText,
  value,
  onChange,
  placeholder = 'Type or paste markdown...',
  descriptionText,
  isRequired,
  errors,
  dataTestId,
  name,
  minRows = MIN_ROWS,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mode, setMode] = useState<Mode>('edit');

  const previewHtml = useMemo(() => renderMarkdown(value || ''), [value]);
  const wordCount = useMemo(() => countWords(value), [value]);
  const charCount = value.length;

  const applyAction = (action: ToolbarAction) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.slice(start, end);

    let nextValue = value;
    let nextSelStart = start;
    let nextSelEnd = end;

    if (action.kind === 'wrap') {
      const inner = selected || (action.placeholder ?? '');
      const insertion = `${action.before}${inner}${action.after}`;
      nextValue = value.slice(0, start) + insertion + value.slice(end);
      const innerStart = start + action.before.length;
      nextSelStart = innerStart;
      nextSelEnd = innerStart + inner.length;
    } else if (action.kind === 'linePrefix') {
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const lineEnd = value.indexOf('\n', end);
      const sliceEnd = lineEnd === -1 ? value.length : lineEnd;
      const block = value.slice(lineStart, sliceEnd);
      const transformed = block
        .split('\n')
        .map((line) => transformLineForPrefix(line, action.prefix))
        .join('\n');
      nextValue = value.slice(0, lineStart) + transformed + value.slice(sliceEnd);
      const firstLineLength = transformed.split('\n')[0].length;
      nextSelStart = lineStart + firstLineLength;
      nextSelEnd = lineStart + transformed.length;
    } else if (action.kind === 'codeBlock') {
      const inner = selected || 'code';
      const needsLeadingNewline = start > 0 && value[start - 1] !== '\n';
      const needsTrailingNewline = end < value.length && value[end] !== '\n';
      const insertion = `${needsLeadingNewline ? '\n' : ''}\`\`\`\n${inner}\n\`\`\`${needsTrailingNewline ? '\n' : ''}`;
      nextValue = value.slice(0, start) + insertion + value.slice(end);
      const innerStart = start + (needsLeadingNewline ? 1 : 0) + 4;
      nextSelStart = innerStart;
      nextSelEnd = innerStart + inner.length;
    } else if (action.kind === 'link') {
      const label = selected || 'link text';
      const urlPlaceholder = 'https://';
      const insertion = `[${label}](${urlPlaceholder})`;
      nextValue = value.slice(0, start) + insertion + value.slice(end);
      nextSelStart = start + `[${label}](`.length;
      nextSelEnd = nextSelStart + urlPlaceholder.length;
    }

    onChange(nextValue);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(nextSelStart, nextSelEnd);
    });
  };

  const handleToolbarClick = (event: MouseEvent<HTMLButtonElement>, action: ToolbarAction) => {
    event.preventDefault();
    applyAction(action);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault();
      const ta = event.currentTarget;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const next = value.slice(0, start) + '  ' + value.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(start + 2, start + 2);
      });
      return;
    }

    if (event.key === 'Enter') {
      const ta = event.currentTarget;
      const start = ta.selectionStart;
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const currentLine = value.slice(lineStart, start);
      const bulletMatch = currentLine.match(/^(\s*)([-*])\s+(.*)$/);
      const numberedMatch = currentLine.match(/^(\s*)(\d+)\.\s+(.*)$/);

      if (bulletMatch && bulletMatch[3] === '') {
        event.preventDefault();
        const next = value.slice(0, lineStart) + value.slice(start);
        onChange(next);
        requestAnimationFrame(() => {
          ta.focus();
          ta.setSelectionRange(lineStart, lineStart);
        });
        return;
      }
      if (numberedMatch && numberedMatch[3] === '') {
        event.preventDefault();
        const next = value.slice(0, lineStart) + value.slice(start);
        onChange(next);
        requestAnimationFrame(() => {
          ta.focus();
          ta.setSelectionRange(lineStart, lineStart);
        });
        return;
      }
      if (bulletMatch) {
        event.preventDefault();
        const insertion = `\n${bulletMatch[1]}${bulletMatch[2]} `;
        const next = value.slice(0, start) + insertion + value.slice(start);
        onChange(next);
        const caret = start + insertion.length;
        requestAnimationFrame(() => {
          ta.focus();
          ta.setSelectionRange(caret, caret);
        });
        return;
      }
      if (numberedMatch) {
        event.preventDefault();
        const nextNumber = Number(numberedMatch[2]) + 1;
        const insertion = `\n${numberedMatch[1]}${nextNumber}. `;
        const next = value.slice(0, start) + insertion + value.slice(start);
        onChange(next);
        const caret = start + insertion.length;
        requestAnimationFrame(() => {
          ta.focus();
          ta.setSelectionRange(caret, caret);
        });
      }
    }
  };

  return (
    <InputField
      labelText={labelText}
      isRequired={isRequired}
      errors={errors}
      descriptionText={descriptionText}
      name={name}
      dataTestId={dataTestId}
    >
      <div className="border-info-300 bg-info-25 focus-within:border-primary-400 focus-within:ring-primary-100 overflow-hidden rounded-md border shadow-xs focus-within:ring-2">
        <div className="border-info-200 bg-info-50 flex items-center justify-between gap-1 border-b px-2 py-1.5">
          <div className="flex flex-wrap items-center gap-0.5">
            {TOOLBAR_ACTIONS.map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={(e) => handleToolbarClick(e, action)}
                title={action.label}
                aria-label={action.label}
                disabled={mode === 'preview'}
                data-testid={`markdown-editor:toolbar:${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={clsx(
                  'text-info-600 hover:bg-info-100 hover:text-info-900 inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-sm transition-colors',
                  mode === 'preview' && 'cursor-not-allowed opacity-40 hover:bg-transparent',
                )}
              >
                <IconMdi path={action.iconPath} size={0.75} centerVertical={false} />
              </button>
            ))}
          </div>
          <div className="bg-info-100 flex items-center gap-0.5 rounded-sm p-0.5">
            <button
              type="button"
              onClick={() => setMode('edit')}
              className={clsx(
                'cursor-pointer rounded-sm px-2 py-1 text-xs font-medium transition-colors',
                mode === 'edit' ? 'text-info-900 bg-white shadow-xs' : 'text-info-500 hover:text-info-700',
              )}
              data-testid="markdown-editor:mode-edit"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => setMode('preview')}
              className={clsx(
                'cursor-pointer rounded-sm px-2 py-1 text-xs font-medium transition-colors',
                mode === 'preview' ? 'text-info-900 bg-white shadow-xs' : 'text-info-500 hover:text-info-700',
              )}
              data-testid="markdown-editor:mode-preview"
            >
              Preview
            </button>
          </div>
        </div>

        {mode === 'edit' ? (
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={minRows}
            spellCheck={false}
            className="text-info-900 placeholder:text-info-400 block w-full resize-y border-0 bg-white px-4 py-3 font-mono text-[13px] leading-relaxed focus:outline-none"
            data-testid={dataTestId ?? 'markdown-editor:textarea'}
            name={name}
          />
        ) : (
          <div
            className={clsx(
              'markdown-preview text-info-900 prose prose-sm max-w-none overflow-y-auto bg-white px-4 py-3 text-sm leading-relaxed',
              '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
              '[&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-xl [&_h1]:font-semibold',
              '[&_h2]:mt-3 [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold',
              '[&_h3]:mt-3 [&_h3]:mb-1 [&_h3]:text-base [&_h3]:font-semibold',
              '[&_p]:my-2',
              '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6',
              '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6',
              '[&_li]:my-0.5',
              '[&_blockquote]:border-info-300 [&_blockquote]:text-info-600 [&_blockquote]:my-2 [&_blockquote]:border-l-4 [&_blockquote]:pl-3 [&_blockquote]:italic',
              '[&_code]:bg-info-100 [&_code]:text-info-800 [&_code]:rounded-sm [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs',
              '[&_pre]:bg-info-50 [&_pre]:border-info-200 [&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:border [&_pre]:p-3',
              '[&_pre_code]:block [&_pre_code]:bg-transparent [&_pre_code]:p-0',
              '[&_a]:text-primary-600 hover:[&_a]:text-primary-700 [&_a]:underline',
            )}
            style={{ minHeight: `${minRows * 1.75}rem` }}
            data-testid="markdown-editor:preview"
            dangerouslySetInnerHTML={
              value.trim()
                ? { __html: previewHtml }
                : { __html: '<p class="text-info-400 italic">Nothing to preview</p>' }
            }
          />
        )}

        <div className="border-info-100 text-info-500 flex items-center justify-between border-t bg-white px-3 py-1.5 text-xs">
          <span>Markdown</span>
          <span data-testid="markdown-editor:counts">
            {wordCount} word{wordCount === 1 ? '' : 's'} · {charCount} char{charCount === 1 ? '' : 's'}
          </span>
        </div>
      </div>
    </InputField>
  );
};
